import { useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

/** Порог вертикального свайпа вверх, после которого уведомление закрывается, px. */
const SWIPE_UP_DISMISS_THRESHOLD_PX = 60

type SwipeUpHandlers = {
  onPointerDown: (event: ReactPointerEvent) => void
  onPointerMove: (event: ReactPointerEvent) => void
  onPointerUp: (event: ReactPointerEvent) => void
  onPointerCancel: (event: ReactPointerEvent) => void
}

/**
 * Свайп вверх закрывает уведомление — вертикальный аналог горизонтального свайпа старого
 * тоста. Тянуть можно только вверх (уведомление и так сверху экрана), вниз не пускаем.
 */
export function useNotificationSwipeUp(onDismiss: () => void): {
  translateY: number
  isDragging: boolean
  resetDrag: () => void
  swipeHandlers: SwipeUpHandlers
} {
  const [translateY, setTranslateY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startYRef = useRef(0)
  const pointerIdRef = useRef<number | null>(null)

  const resetDrag = () => {
    setIsDragging(false)
    setTranslateY(0)
  }

  const onPointerDown: SwipeUpHandlers['onPointerDown'] = (event) => {
    pointerIdRef.current = event.pointerId
    startYRef.current = event.clientY
    setIsDragging(true)
  }

  const onPointerMove: SwipeUpHandlers['onPointerMove'] = (event) => {
    if (pointerIdRef.current !== event.pointerId) return
    const delta = event.clientY - startYRef.current
    setTranslateY(Math.min(0, delta))
  }

  const finishDrag: SwipeUpHandlers['onPointerUp'] = (event) => {
    if (pointerIdRef.current !== event.pointerId) return

    const delta = event.clientY - startYRef.current
    pointerIdRef.current = null

    if (delta < -SWIPE_UP_DISMISS_THRESHOLD_PX) {
      // Драг не сбрасываем — уход анимируется дальше от текущей позиции (см. эффект
      // в AnimatedNotification, который снимет isDragging, когда придёт isLeaving).
      onDismiss()
      return
    }

    resetDrag()
  }

  return {
    translateY,
    isDragging,
    resetDrag,
    swipeHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finishDrag,
      onPointerCancel: finishDrag,
    },
  }
}
